import { camelCase } from '../utils/changeCase';
import { jsonTryParse } from '../utils/json';
import { get } from '../utils';
import { getOrchestratedDataset } from '../utils/element';

export const KSYS_GLOBAL_VARIABLES_WINDOW_KEY = '__ksysGlobalVariables__';

export type AppParamType = 'inputs' | 'configuration' | 'theme';
export interface AppParamRequestItem {
	type: AppParamType;
	key: string;
}

export interface AppParamInstance {
	type: AppParamType;
	key: string;
	instance?: any;
}

export interface ParamValues<TInputs, TConfiguration, TTheme> {
	inputs?: TInputs;
	configuration?: TConfiguration;
	theme?: TTheme;
}

export class AppParamsHelper<TInputs extends {} = any, TConfiguration = any, TTheme = any> {
	static types: AppParamType[] = ['configuration', 'theme'];
	element?: HTMLElement;
	url?: string;
	values?: ParamValues<TInputs, TConfiguration, TTheme> = {};
	rawAppParams?: any;
	staticInputs?: object;
	constructor(element?: HTMLElement, url?: string) {
		this.element = element;
		this.url = url;
		this.rawAppParams = this.getAppParamsFromElement();
		this.loadValues();
	}

	async loadValues() {
		this.loadInputs();
		const appParamRequestItems = this.getAppParamRequestItems(AppParamsHelper.types, this.rawAppParams);
		const appParamInstances = await this.fetchAppParamInstances(appParamRequestItems);
		for (const item of appParamInstances) {
			const { type, instance } = item;
			if (!this.values) {
				this.values = {};
			}
			this.values[type] = instance;
		}

		// Need to handle the fetching of input params from the URL and global state
		// console.log({ values: this.values });
	}

	getAppParamRequestItems(types: AppParamType[], rawAppParams: any): AppParamRequestItem[] {
		const response: AppParamRequestItem[] = [];
		for (const type of types) {
			const instanceKey = `${type}Instance`;
			const keyKey = `${type}Key`;
			if (!rawAppParams[instanceKey]) {
				if (rawAppParams[keyKey]) {
					response.push({
						type,
						key: rawAppParams[keyKey],
					});
				}
			}
			else {
				if (!this.values) {
					this.values = {};
				}
				this.values[type] = rawAppParams[instanceKey];
			}
		}
		return response;
	}

	loadInputs() {
		this.staticInputs = this.loadPrefixedFieldsFromElement('input', 'inputs', this.rawAppParams);
	}

	loadInputsFromConfiguration(inputs: object) {
		// The order is important to let the existing values take precedence as they would be explicit
		this.values.inputs = {
			...(this.values.inputs || {}) as any,
			...inputs,
			...(this.staticInputs || {}) as any,
		};
	}

	loadPrefixedFieldsFromElement(prefix: string, valuesKey: string, rawAppParams: any) {
		const keys = Object.keys(rawAppParams || {}).filter(key => key.startsWith(prefix));
		const values = keys.reduce((acc: { [key: string]: any }, key) => {
			const value = rawAppParams[key];
			const newKey = camelCase(key.substr(prefix.length));
			acc[newKey] = value;
			return acc;
		}, {});
		this.values[valuesKey] = values;
		return this.values[valuesKey];
	}

	getAppParamsFromElement(): any {
		return getOrchestratedDataset(this.element);
	}

	getParamInstance(type: AppParamType) {
		const rawInstance = `${type}Raw`;
		if (rawInstance) {
			return rawInstance;
		}
		const key = `${type}Key`;

	}
	async fetchAppParamInstances(items: AppParamRequestItem[]): Promise<AppParamInstance[]> {
		// DUMMY DATA
		return items.map((item) => {
			return Object.assign({}, item, {
				instance: {

				},
			});
		});
	}

	get inputs(): TInputs | undefined {
		const response: TInputs = {} as any;
		const configuration = this.configuration;
		if (configuration) {
			// THIS MUST BE CONVERTED TO USE THE INPUTS ON THE APPLICATION INSTANCE
			// const { inputMapping = {}, inputs } = configuration;
			// const { urlFormat = '' } = inputMapping || {} as IInputMapping;
			// const inputsByMappingId = pivotCollection(inputs && inputs.values, element => element.inputMappingId);
			// (inputMapping.mappings || []).forEach((mappingItem) => {
			// 	const { id } = mappingItem;
			// 	const existingInput = inputsByMappingId[id];
			// 	if (existingInput) {
			// 		response[id] = existingInput.value;
			// 	}
			// 	else {
			// 		if (mappingItem.type === 'queryString') {
			// 			response[id] = getQueryStringValue(mappingItem.mapping, this.url);
			// 		}
			// 		else if (mappingItem.type === 'url') {
			// 			response[id] = getUrlValueFromFormat(mappingItem.mapping, urlFormat, this.url);
			// 		}
			// 		else if (mappingItem.type === 'hardCoded') {
			// 			if (mappingItem.mapping && mappingItem.mapping.value) {
			// 				response[id] = mappingItem.mapping.value;
			// 			}
			// 		}
			// 		else if (mappingItem.type === 'global') {
			// 			response[id] = getGlobalValue(mappingItem.mapping);
			// 		}
			// 	}
			// });
		}
		return Object.assign(response, (this.values && this.values.inputs) || {});
	}
	get configuration(): TConfiguration | undefined {
		return this.values && this.values.configuration;
	}
	get theme(): TTheme | undefined {
		return this.values && this.values.theme;
	}
}